import axiosInstance from './axiosInstance';

/**
 * API functions for vehicle analytics and reporting
 */

/**
 * Get dashboard summary metrics
 * @param {string} location - Optional location filter
 */
export const getDashboardMetrics = async (location) => {
  try {
    const params = location ? { location } : {};
    const response = await axiosInstance.get('/Corrohealth/vehicles/analytics/dashboard', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get vehicle report for a specific vehicle
 * @param {string} vehicleId - Vehicle UUID
 * @param {string} startDate - Start date in ISO format
 * @param {string} endDate - End date in ISO format
 */
export const getVehicleReport = async (vehicleId, startDate, endDate) => {
  try {
    const response = await axiosInstance.get(
      `/Corrohealth/vehicles/analytics/report/${vehicleId}`,
      {
        params: { startDate, endDate }
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get reports for all vehicles
 * @param {string} startDate - Start date in ISO format
 * @param {string} endDate - End date in ISO format
 * @param {string} location - Optional location filter
 */
export const getAllVehicleReports = async (startDate, endDate, location) => {
  try {
    const params = { startDate, endDate };
    if (location) params.location = location;

    const response = await axiosInstance.get('/Corrohealth/vehicles/analytics/reports', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get usage chart data for visualizations
 * @param {string} startDate - Start date in ISO format
 * @param {string} endDate - End date in ISO format
 * @param {string} location - Optional location filter
 */
export const getUsageCharts = async (startDate, endDate, location) => {
  try {
    const params = { startDate, endDate };
    if (location) params.location = location;

    const response = await axiosInstance.get('/Corrohealth/vehicles/analytics/charts', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Log a vehicle usage event
 * @param {Object} usageLog - Vehicle usage log data
 */
export const logVehicleUsage = async (usageLog) => {
  try {
    const response = await axiosInstance.post('/Corrohealth/vehicles/analytics/log', usageLog);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get usage logs for a specific vehicle
 * @param {string} vehicleId - Vehicle UUID
 * @param {string} startDate - Start date in ISO format
 * @param {string} endDate - End date in ISO format
 */
export const getVehicleUsageLogs = async (vehicleId, startDate, endDate) => {
  try {
    const response = await axiosInstance.get(
      `/Corrohealth/vehicles/analytics/logs/${vehicleId}`,
      {
        params: { startDate, endDate }
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all usage logs within date range
 * @param {string} startDate - Start date in ISO format
 * @param {string} endDate - End date in ISO format
 */
export const getAllUsageLogs = async (startDate, endDate) => {
  try {
    const response = await axiosInstance.get('/Corrohealth/vehicles/analytics/logs', {
      params: { startDate, endDate }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get user-based vehicle analytics with capacity tracking
 * @param {string} location - Optional location filter
 */
export const getUserAnalytics = async (location, startDate, endDate) => {
  try {
    const params = {};
    if (location)   params.location  = location;
    if (startDate)  params.startDate = startDate;
    if (endDate)    params.endDate   = endDate;
    const response = await axiosInstance.get('/Corrohealth/vehicles/analytics/user-analytics', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get scan analytics for dashboard
 * Returns daily scans, route-wise breakdown, vehicle-wise breakdown, etc.
 */
export const getScanAnalytics = async (location, startDate, endDate) => {
  try {
    const params = {};
    if (location) params.location = location;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await axiosInstance.get('/Corrohealth/vehicles/analytics/scan-stats', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get list of locations the current user has access to.
 * Used to populate location filter dropdown for multi-location admins.
 */
export const getAccessibleLocations = async () => {
  try {
    const response = await axiosInstance.get('/Corrohealth/vehicles/analytics/accessible-locations');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get comprehensive employee travel report with department-wise analysis
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @param {string} location - Optional location filter
 */
export const getEmployeeTravelReport = async (startDate, endDate, location) => {
  try {
    const params = { startDate, endDate };
    if (location) params.location = location;
    const response = await axiosInstance.get('/Corrohealth/vehicles/analytics/travel-report', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get detailed scan log report with filters
 * @param {Object} filters - { startDate, endDate, location, timing, route, empId, vehicle }
 */
export const getScanLogReport = async (filters) => {
  try {
    const params = {};
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.location) params.location = filters.location;
    if (filters.timing) params.timing = filters.timing;
    if (filters.route) params.route = filters.route;
    if (filters.empId) params.empId = filters.empId;
    if (filters.vehicle) params.vehicle = filters.vehicle;
    if (filters.scannedBy) params.scannedBy = filters.scannedBy;
    const response = await axiosInstance.get('/Corrohealth/vehicles/analytics/scan-logs', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getNoShowReport = async (startDate, endDate, location) => {
  try {
    const params = { startDate, endDate };
    if (location) params.location = location;
    const response = await axiosInstance.get('/Corrohealth/vehicles/analytics/no-show-report', { params });
    return response.data;
  } catch (error) { throw error; }
};

export const getAttendanceReport = async (startDate, endDate, location) => {
  try {
    const params = { startDate, endDate };
    if (location) params.location = location;
    const response = await axiosInstance.get('/Corrohealth/vehicles/analytics/attendance-report', { params });
    return response.data;
  } catch (error) { throw error; }
};

export const getVehicleUtilization = async (startDate, endDate, location) => {
  try {
    const params = { startDate, endDate };
    if (location) params.location = location;
    const response = await axiosInstance.get('/Corrohealth/vehicles/analytics/vehicle-utilization', { params });
    return response.data;
  } catch (error) { throw error; }
};

export const getGatemanReport = async (startDate, endDate, location) => {
  try {
    const params = { startDate, endDate };
    if (location) params.location = location;
    const response = await axiosInstance.get('/Corrohealth/vehicles/analytics/gateman-report', { params });
    return response.data;
  } catch (error) { throw error; }
};

export const getMonthComparison = async (location) => {
  try {
    const params = location ? { location } : {};
    const response = await axiosInstance.get('/Corrohealth/vehicles/analytics/month-comparison', { params });
    return response.data;
  } catch (error) { throw error; }
};

export const getLocationSummary = async (startDate, endDate) => {
  try {
    const response = await axiosInstance.get('/Corrohealth/vehicles/analytics/location-summary', {
      params: { startDate, endDate }
    });
    return response.data;
  } catch (error) { throw error; }
};

export const getLocationDepartment = async (startDate, endDate, location) => {
  try {
    const response = await axiosInstance.get('/Corrohealth/vehicles/analytics/location-department', {
      params: { startDate, endDate, location }
    });
    return response.data;
  } catch (error) { throw error; }
};

export default {
  getDashboardMetrics, getVehicleReport, getAllVehicleReports, getUsageCharts,
  logVehicleUsage, getVehicleUsageLogs, getAllUsageLogs, getUserAnalytics,
  getScanAnalytics, getAccessibleLocations, getEmployeeTravelReport, getScanLogReport,
  getNoShowReport, getAttendanceReport, getVehicleUtilization, getGatemanReport, getMonthComparison,
  getLocationSummary, getLocationDepartment,
};
